import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  DocumentReference,
} from "firebase/firestore";
import { db } from "@/firebase";
import { useAuth } from "@/contexts/AuthContext";

type DashboardCard = {
  id: string;
  title: string;
  description: string;
  icon: string;
  path: string;
};

const ICON_OPTIONS = [
  { value: "ri-book-mark-line", label: "教材" },
  { value: "ri-file-list-3-line", label: "回答" },
  { value: "ri-user-settings-line", label: "ユーザー" },
  { value: "ri-question-answer-line", label: "問題" },
  { value: "ri-shopping-cart-line", label: "注文" },
  { value: "ri-notification-3-line", label: "お知らせ" },
  { value: "ri-bank-line", label: "入金" },
  { value: "ri-mail-line", label: "メール" },
  { value: "ri-bar-chart-grouped-line", label: "分析" },
  { value: "ri-settings-3-line", label: "設定" },
  { value: "ri-dashboard-line", label: "ダッシュボード" },
  { value: "ri-link", label: "リンク" },
];

const DEFAULT_DASHBOARD_CARDS: DashboardCard[] = [
  {
    id: "default-shikaku",
    title: "資格管理",
    description: "資格データの追加・編集・削除を行います。",
    icon: "ri-book-mark-line",
    path: "/admin/shikaku",
  },
  {
    id: "default-onlinetest",
    title: "回答管理",
    description: "受験者の回答結果を確認・管理します。",
    icon: "ri-file-list-3-line",
    path: "/admin/onlinetest",
  },
  {
    id: "default-users",
    title: "ユーザー管理",
    description: "登録ユーザーの一覧確認と管理を行います。",
    icon: "ri-user-settings-line",
    path: "/admin/users",
  },
  {
    id: "default-questions",
    title: "問題管理",
    description: "試験問題の作成・修正・整理を行います。",
    icon: "ri-question-answer-line",
    path: "/admin/questions",
  },
  {
    id: "default-orders",
    title: "注文管理",
    description: "購入情報や割り当て状況を管理します。",
    icon: "ri-shopping-cart-line",
    path: "/admin/orders",
  },
  {
    id: "default-information",
    title: "お知らせ管理",
    description: "お知らせの投稿・更新・掲載管理を行います。",
    icon: "ri-notification-3-line",
    path: "/admin/information",
  },
];

const iconAccentClasses = [
  "bg-blue-50 text-blue-600",
  "bg-green-50 text-green-600",
  "bg-purple-50 text-purple-600",
  "bg-orange-50 text-orange-600",
  "bg-pink-50 text-pink-600",
  "bg-yellow-50 text-yellow-600",
  "bg-cyan-50 text-cyan-600",
  "bg-indigo-50 text-indigo-600",
  "bg-rose-50 text-rose-600",
  "bg-emerald-50 text-emerald-600",
];

interface TaskMemo {
  id: string;
  task?: string | null;
  tasklimit?: Timestamp | Date | null;
  account?: DocumentReference | string | null;
  TGTuser?: DocumentReference | string | null;
  TGTorder?: DocumentReference | string | null;
  text?: string;
  CreateAt?: Timestamp | Date | null;
}

interface TaskMemoRow extends TaskMemo {
  accountName: string;
  userName: string;
  orderNumber: string;
  userId: string;
  orderId: string;
}

interface AdminUserOption {
  id: string;
  displayName: string;
}

const createCardId = () => `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [taskMemos, setTaskMemos] = useState<TaskMemoRow[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUserOption[]>([]);
  const [selectedMemo, setSelectedMemo] = useState<TaskMemoRow | null>(null);
  const [memoSaving, setMemoSaving] = useState(false);
  const [editTaskStatus, setEditTaskStatus] = useState<"active" | "done" | null>(null);
  const [editTaskLimit, setEditTaskLimit] = useState("");
  const [editAccountId, setEditAccountId] = useState("");
  const [editText, setEditText] = useState("");

  const [dashboardCards, setDashboardCards] = useState<DashboardCard[]>(DEFAULT_DASHBOARD_CARDS);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsSaving, setCardsSaving] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isCardEditMode, setIsCardEditMode] = useState(false);
  const [cardForm, setCardForm] = useState({
    title: "",
    description: "",
    path: "",
    icon: ICON_OPTIONS[0].value,
  });

  const toDateInputValue = (value?: Timestamp | Date | string | null) => {
    if (!value) return "";
    const date =
      value instanceof Timestamp
        ? value.toDate()
        : value instanceof Date
          ? value
          : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (val: number) => String(val).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const formatMemoDate = (value?: Timestamp | Date | null) => {
    if (!value) return "-";
    if (value instanceof Timestamp) return value.toDate().toLocaleDateString("ja-JP");
    if (value instanceof Date) return value.toLocaleDateString("ja-JP");
    return String(value);
  };

  const resolveUserName = async (userRef?: DocumentReference | string | null) => {
    if (!userRef) return { name: "", id: "" };
    try {
      const resolvedRef = typeof userRef === "string" ? doc(db, "User", userRef) : userRef;
      const snapshot = await getDoc(resolvedRef);
      if (!snapshot.exists()) return { name: "", id: resolvedRef.id };
      const userData = snapshot.data();
      return {
        name: userData.display_name || userData.display_Name || "",
        id: resolvedRef.id,
      };
    } catch (err) {
      console.error("Error fetching user name:", err);
      return { name: "", id: "" };
    }
  };

  const resolveOrderNumber = async (orderRef?: DocumentReference | string | null) => {
    if (!orderRef) return { orderNumber: "", id: "" };
    try {
      const resolvedRef = typeof orderRef === "string" ? doc(db, "Order", orderRef) : orderRef;
      const snapshot = await getDoc(resolvedRef);
      if (!snapshot.exists()) return { orderNumber: "", id: resolvedRef.id };
      const orderData = snapshot.data();
      return {
        orderNumber: orderData.orderNumber || "",
        id: resolvedRef.id,
      };
    } catch (err) {
      console.error("Error fetching order number:", err);
      return { orderNumber: "", id: "" };
    }
  };

  const loadDashboardCards = async () => {
    if (!userId) {
      setDashboardCards(DEFAULT_DASHBOARD_CARDS);
      return;
    }
    setCardsLoading(true);
    setCardError(null);
    try {
      const userSnapshot = await getDoc(doc(db, "User", userId));
      if (!userSnapshot.exists()) {
        setDashboardCards(DEFAULT_DASHBOARD_CARDS);
        return;
      }
      const data = userSnapshot.data();
      const savedCards = Array.isArray(data.adminDashboardCards)
        ? data.adminDashboardCards
            .filter((card: any) => card && typeof card.title === "string" && typeof card.path === "string")
            .map((card: any, index: number) => ({
              id: card.id || `loaded-${index}`,
              title: card.title || "",
              description: card.description || "",
              path: card.path || "",
              icon: card.icon || ICON_OPTIONS[0].value,
            }))
        : [];
      setDashboardCards(savedCards.length > 0 ? savedCards : DEFAULT_DASHBOARD_CARDS);
    } catch (error: any) {
      console.error("Error loading dashboard cards:", error);
      setCardError(error?.message || "ダッシュボードカードの読み込みに失敗しました");
      setDashboardCards(DEFAULT_DASHBOARD_CARDS);
    } finally {
      setCardsLoading(false);
    }
  };

  const saveDashboardCards = async (nextCards: DashboardCard[]) => {
    if (!userId) {
      alert("ユーザー情報が取得できないため保存できません");
      return false;
    }
    setCardsSaving(true);
    setCardError(null);
    try {
      await updateDoc(doc(db, "User", userId), {
        adminDashboardCards: nextCards,
      });
      setDashboardCards(nextCards);
      return true;
    } catch (error: any) {
      console.error("Error saving dashboard cards:", error);
      setCardError(error?.message || "ダッシュボードカードの保存に失敗しました");
      return false;
    } finally {
      setCardsSaving(false);
    }
  };

  useEffect(() => {
    loadDashboardCards();
  }, [userId]);

  useEffect(() => {
    const fetchAdminUsers = async () => {
      try {
        const adminSnapshot = await getDocs(query(collection(db, "User"), where("Role", "==", "admin")));
        const adminOptions = adminSnapshot.docs.map((userDoc) => {
          const data = userDoc.data();
          const displayName = data.display_name || data.display_Name || userDoc.id;
          return {
            id: userDoc.id,
            displayName,
          };
        });
        adminOptions.sort((a, b) => a.displayName.localeCompare(b.displayName, "ja"));
        setAdminUsers(adminOptions);
      } catch (err) {
        console.error("Error fetching admin users:", err);
        setAdminUsers([]);
      }
    };

    fetchAdminUsers();
  }, []);

  useEffect(() => {
    const fetchTaskMemos = async () => {
      setTaskLoading(true);
      setTaskError(null);
      try {
        const memoQuery = query(
          collection(db, "Memo"),
          where("task", "==", "active"),
          orderBy("tasklimit", "asc")
        );
        const memoSnapshot = await getDocs(memoQuery);
        const memoDocs = memoSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })) as TaskMemo[];
        const resolvedRows = await Promise.all(
          memoDocs.map(async (memo) => {
            const [accountInfo, userInfo, orderInfo] = await Promise.all([
              resolveUserName(memo.account ?? null),
              resolveUserName(memo.TGTuser ?? null),
              resolveOrderNumber(memo.TGTorder ?? null),
            ]);
            return {
              ...memo,
              accountName: accountInfo.name,
              userName: userInfo.name,
              orderNumber: orderInfo.orderNumber,
              userId: userInfo.id,
              orderId: orderInfo.id,
            };
          })
        );
        setTaskMemos(resolvedRows);
      } catch (err: any) {
        console.error("Error fetching task memos:", err);
        setTaskError(err?.message || "未完了タスクの取得に失敗しました");
      } finally {
        setTaskLoading(false);
      }
    };

    fetchTaskMemos();
  }, []);

  const openMemoDetail = (memo: TaskMemoRow) => {
    setSelectedMemo(memo);
    setEditTaskStatus((memo.task as "active" | "done" | null) ?? null);
    setEditTaskLimit(toDateInputValue(memo.tasklimit));
    const accountId = memo.account instanceof DocumentReference ? memo.account.id : memo.account ?? "";
    setEditAccountId(accountId || "");
    setEditText(memo.text || "");
  };

  const updateMemoDetail = async () => {
    if (!selectedMemo) return;
    if (editTaskStatus === "active") {
      if (!editTaskLimit) {
        alert("タスク期日を入力してください");
        return;
      }
      if (!editAccountId) {
        alert("担当者を選択してください");
        return;
      }
    }
    setMemoSaving(true);
    try {
      const taskLimitDate = editTaskLimit ? new Date(`${editTaskLimit}T00:00:00`) : null;
      const memoRef = doc(db, "Memo", selectedMemo.id);
      const shouldIncludeTaskDetails = editTaskStatus === "active" || editTaskStatus === "done";
      const now = new Date();
      now.setSeconds(0, 0);
      const payload = {
        task: editTaskStatus ?? null,
        tasklimit: shouldIncludeTaskDetails && taskLimitDate ? Timestamp.fromDate(taskLimitDate) : null,
        account: shouldIncludeTaskDetails && editAccountId ? doc(db, "User", editAccountId) : null,
        text: editText.trim(),
        ...(editTaskStatus === "done" ? { update: Timestamp.fromDate(now) } : {}),
      };
      await updateDoc(memoRef, payload);
      const accountName = adminUsers.find((user) => user.id === editAccountId)?.displayName || "";
      const updatedMemo: TaskMemoRow = {
        ...selectedMemo,
        task: payload.task,
        tasklimit: payload.tasklimit ?? null,
        account: payload.account ?? null,
        text: payload.text,
        accountName: payload.task === "active" ? accountName : "",
      };
      setTaskMemos((prev) => {
        if (updatedMemo.task !== "active") {
          return prev.filter((memo) => memo.id !== updatedMemo.id);
        }
        return prev.map((memo) => (memo.id === updatedMemo.id ? updatedMemo : memo));
      });
      setSelectedMemo(updatedMemo.task === "active" ? updatedMemo : null);
    } catch (err: any) {
      console.error("Error updating memo:", err);
      alert(err?.message || "メモの更新に失敗しました");
    } finally {
      setMemoSaving(false);
    }
  };

  const sortedTaskMemos = useMemo(
    () =>
      [...taskMemos].sort((a, b) => {
        const aTime = a.tasklimit
          ? a.tasklimit instanceof Timestamp
            ? a.tasklimit.toDate().getTime()
            : new Date(a.tasklimit).getTime()
          : Number.MAX_SAFE_INTEGER;
        const bTime = b.tasklimit
          ? b.tasklimit instanceof Timestamp
            ? b.tasklimit.toDate().getTime()
            : new Date(b.tasklimit).getTime()
          : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      }),
    [taskMemos]
  );

  const openCreateCardModal = () => {
    setEditingCardId(null);
    setCardForm({
      title: "",
      description: "",
      path: "",
      icon: ICON_OPTIONS[0].value,
    });
    setIsCardModalOpen(true);
  };

  const openEditCardModal = (card: DashboardCard) => {
    setEditingCardId(card.id);
    setCardForm({
      title: card.title,
      description: card.description,
      path: card.path,
      icon: card.icon,
    });
    setIsCardModalOpen(true);
  };

  const handleSubmitCard = async () => {
    const title = cardForm.title.trim();
    const description = cardForm.description.trim();
    const path = cardForm.path.trim();

    if (!title || !path) {
      alert("タイトルとリンク先は必須です");
      return;
    }

    const nextCards = editingCardId
      ? dashboardCards.map((card) =>
          card.id === editingCardId
            ? {
                ...card,
                title,
                description,
                path,
                icon: cardForm.icon,
              }
            : card
        )
      : [
          ...dashboardCards,
          {
            id: createCardId(),
            title,
            description,
            path,
            icon: cardForm.icon,
          },
        ];

    const saved = await saveDashboardCards(nextCards);
    if (saved) {
      setIsCardModalOpen(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    const target = dashboardCards.find((card) => card.id === cardId);
    if (!target) return;
    const confirmed = window.confirm(`「${target.title}」を削除しますか？`);
    if (!confirmed) return;

    const nextCards = dashboardCards.filter((card) => card.id !== cardId);
    await saveDashboardCards(nextCards.length ? nextCards : DEFAULT_DASHBOARD_CARDS);
  };

  const handleCardNavigate = (path: string) => {
    if (!path) return;
    if (/^https?:\/\//i.test(path)) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(path);
  };

  return (
    <div className="space-y-8">
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
        <p className="text-sm text-gray-500 mb-2">ようこそ</p>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">管理ダッシュボード</h1>
        <p className="text-gray-600">
          ユーザーごとにクイックリンクカードを登録できます。左のサイドバーでいつでもページを切り替えられます。
        </p>
      </section>

      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">クイックリンク</h2>
            <p className="text-sm text-gray-500 mt-1">カード形式で登録・編集・削除できます（ユーザーごとに保存）。</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsCardEditMode((prev) => !prev)}
              disabled={cardsSaving || cardsLoading}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {isCardEditMode ? "編集終了" : "編集"}
            </button>
            <button
              type="button"
              onClick={openCreateCardModal}
              disabled={cardsSaving || cardsLoading}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:bg-gray-300"
            >
              カード追加
            </button>
          </div>
        </div>

        {cardsLoading ? (
          <div className="text-sm text-gray-500">カードを読み込み中...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            {dashboardCards.map((card, index) => (
              <div
                key={card.id}
                role={!isCardEditMode ? "button" : undefined}
                tabIndex={!isCardEditMode ? 0 : undefined}
                onClick={() => {
                  if (!isCardEditMode) {
                    handleCardNavigate(card.path);
                  }
                }}
                onKeyDown={(event) => {
                  if (!isCardEditMode && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    handleCardNavigate(card.path);
                  }
                }}
                className={`rounded-2xl border border-gray-100 bg-white p-5 md:p-6 shadow-sm transition-all ${
                  isCardEditMode ? "" : "cursor-pointer hover:shadow-md"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${iconAccentClasses[index % iconAccentClasses.length]}`}>
                    <i className={card.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{card.title}</h3>
                      <i className="ri-arrow-right-line text-gray-400" />
                    </div>
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">{card.description || "説明なし"}</p>
                    {isCardEditMode && <p className="mt-2 text-xs text-gray-400 break-all">{card.path}</p>}
                  </div>
                </div>
                {isCardEditMode && (
                  <div className="mt-4 flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => openEditCardModal(card)}
                      className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCard(card.id)}
                      className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                    >
                      削除
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {cardError && <div className="text-sm text-red-500">{cardError}</div>}
      </section>

      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">未完了のタスク</h2>
            <p className="text-sm text-gray-500 mt-1">タスクフラグが active のメモを一覧表示しています。</p>
          </div>
        </div>

        {taskLoading ? (
          <div className="text-sm text-gray-500">読み込み中...</div>
        ) : taskError ? (
          <div className="text-sm text-red-500">{taskError}</div>
        ) : sortedTaskMemos.length === 0 ? (
          <div className="text-sm text-gray-500">未完了のタスクはありません。</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-4 font-medium">タスク期日</th>
                  <th className="py-2 pr-4 font-medium">担当者</th>
                  <th className="py-2 pr-4 font-medium">ユーザー</th>
                  <th className="py-2 pr-4 font-medium">注文番号</th>
                  <th className="py-2 pr-4 font-medium">内容</th>
                  <th className="py-2 pr-2 font-medium">詳細</th>
                </tr>
              </thead>
              <tbody>
                {sortedTaskMemos.map((memo) => (
                  <tr key={memo.id} className="border-b border-gray-50 text-gray-700">
                    <td className="py-3 pr-4">{formatMemoDate(memo.tasklimit)}</td>
                    <td className="py-3 pr-4">{memo.accountName || "-"}</td>
                    <td className="py-3 pr-4">
                      {memo.userId ? (
                        <Link to={`/admin/users/${memo.userId}`} className="text-emerald-600 font-semibold hover:underline">
                          {memo.userName || memo.userId}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      {memo.orderId ? (
                        <Link to={`/admin/orders/${memo.orderId}`} className="text-emerald-600 font-semibold hover:underline">
                          {memo.orderNumber || memo.orderId}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="py-3 pr-4 truncate max-w-xs">{memo.text || "-"}</td>
                    <td className="py-3 pr-2">
                      <button
                        type="button"
                        onClick={() => openMemoDetail(memo)}
                        className="text-emerald-600 font-semibold hover:underline"
                      >
                        詳細
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isCardModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900">{editingCardId ? "カード編集" : "カード追加"}</h3>
            <div className="mt-5 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700" htmlFor="dashboard-card-title">
                  タイトル
                </label>
                <input
                  id="dashboard-card-title"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  value={cardForm.title}
                  onChange={(e) => setCardForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="例: 請求管理"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700" htmlFor="dashboard-card-description">
                  説明
                </label>
                <textarea
                  id="dashboard-card-description"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  rows={3}
                  value={cardForm.description}
                  onChange={(e) => setCardForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="機能の説明を入力"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700" htmlFor="dashboard-card-path">
                    リンク先
                  </label>
                  <input
                    id="dashboard-card-path"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    value={cardForm.path}
                    onChange={(e) => setCardForm((prev) => ({ ...prev, path: e.target.value }))}
                    placeholder="/admin/orders または https://..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700" htmlFor="dashboard-card-icon">
                    アイコン
                  </label>
                  <select
                    id="dashboard-card-icon"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    value={cardForm.icon}
                    onChange={(e) => setCardForm((prev) => ({ ...prev, icon: e.target.value }))}
                  >
                    {ICON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} ({option.value})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                onClick={() => setIsCardModalOpen(false)}
                disabled={cardsSaving}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:bg-gray-300"
                onClick={handleSubmitCard}
                disabled={cardsSaving}
              >
                {cardsSaving ? "保存中..." : editingCardId ? "更新" : "登録"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedMemo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-lg">
            <div className="text-center text-lg font-semibold text-gray-800">メモ詳細</div>
            <div className="mt-6 space-y-4">
              <div className="text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">タスク期日</span>
                  <span className="font-medium text-gray-800">{formatMemoDate(selectedMemo.tasklimit)}</span>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">担当者</span>
                  <span className="font-medium text-gray-800">{selectedMemo.accountName || "-"}</span>
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-semibold text-gray-700">タスクフラグ</span>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="memo-task-flag"
                      checked={editTaskStatus === null}
                      onChange={() => {
                        setEditTaskStatus(null);
                        setEditTaskLimit("");
                        setEditAccountId("");
                      }}
                    />
                    ーー
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="memo-task-flag"
                      checked={editTaskStatus === "active"}
                      onChange={() => setEditTaskStatus("active")}
                    />
                    タスク
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="memo-task-flag"
                      checked={editTaskStatus === "done"}
                      onChange={() => {
                        setEditTaskStatus("done");
                      }}
                    />
                    タスク完了
                  </label>
                </div>
              </div>
              {(editTaskStatus === "active" || editTaskStatus === "done") && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700" htmlFor="task-limit-edit">
                      タスク期日
                    </label>
                    <input
                      id="task-limit-edit"
                      type="date"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      value={editTaskLimit}
                      onChange={(e) => setEditTaskLimit(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700" htmlFor="task-account-edit">
                      担当者
                    </label>
                    <select
                      id="task-account-edit"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      value={editAccountId}
                      onChange={(e) => setEditAccountId(e.target.value)}
                    >
                      <option value="">担当者を選択</option>
                      {adminUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700" htmlFor="memo-text-edit">
                  内容
                </label>
                <textarea
                  id="memo-text-edit"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  rows={4}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={updateMemoDetail}
                disabled={memoSaving}
                className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {memoSaving ? "保存中..." : "メモを更新"}
              </button>
              <button
                type="button"
                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                onClick={() => setSelectedMemo(null)}
                disabled={memoSaving}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
